import React from "react";
import { Link } from "react-router-dom";
import { HeartHandshake, ArrowRight, ShieldCheck } from "lucide-react";
import { creditChallenges, images } from "../mock";
import PageHero from "../components/PageHero";
import ContactForm from "../components/ContactForm";
import { Button } from "../components/ui/button";
import { LenderCard } from "../components/Lender";

const CreditChallenges = () => {
  return (
    <div>
      <PageHero
        eyebrow="A Fresh Start"
        title={`Been told "no"? Let's find your yes.`}
        subtitle={creditChallenges.intro}
        image={images.heroExteriors[2]}
      />

      {/* Reassurance */}
      <section className="max-w-7xl mx-auto px-6 md:px-8 py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <span className="text-xs tracking-luxury uppercase text-gold">Why I Can Help</span>
            <h2 className="font-display text-4xl md:text-5xl text-[#141210] mt-4 mb-6 leading-tight">
              Big banks see a number. I see your whole story.
            </h2>
            <p className="text-[#5c5348] leading-relaxed mb-6">
              Credit challenges are more common than anyone admits — a medical bill, a divorce, a
              rough patch after service. None of it makes you unworthy of a home.
            </p>
            <p className="text-[#5c5348] leading-relaxed">
              Because I work hand-in-hand with a genuinely flexible lender, I can often find a path
              where others slammed the door. It starts with a warm, judgment-free conversation.
            </p>
            <div className="mt-8 inline-flex items-center gap-3 bg-[#f7edcf] rounded-full px-5 py-3">
              <ShieldCheck className="w-5 h-5 text-gold" />
              <span className="text-sm font-medium text-[#141210]">
                No Minimum Credit Score on VA Loans*
              </span>
            </div>
          </div>
          <div className="img-zoom rounded-3xl overflow-hidden shadow-[0_30px_70px_-40px_rgba(84,64,34,0.5)]">
            <img
              src="/whole-story.jpg"
              alt="Family reviewing their home financing options together at home"
              className="w-full h-[500px] object-cover"
            />
          </div>
        </div>
      </section>

      {/* Situations */}
      <section className="bg-[#f4f1ea]">
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-24">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="text-xs tracking-luxury uppercase text-gold">You're Not Alone</span>
            <h2 className="font-display text-4xl md:text-5xl text-[#141210] mt-4">
              Situations I help with every week
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
            {creditChallenges.bullets.map((b) => (
              <div
                key={b.title}
                className="bg-white rounded-2xl p-8 border border-[#efe6d6] hover-lift flex gap-5"
              >
                <div className="w-12 h-12 rounded-full bg-[#f7edcf] flex items-center justify-center shrink-0">
                  <HeartHandshake className="w-6 h-6 text-gold" />
                </div>
                <div>
                  <h3 className="font-display text-2xl text-[#141210] mb-2">{b.title}</h3>
                  <p className="text-[#5c5348] leading-relaxed text-sm">{b.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Steps */}
      <section className="max-w-7xl mx-auto px-6 md:px-8 py-24">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <span className="text-xs tracking-luxury uppercase text-gold">The Path Home</span>
          <h2 className="font-display text-4xl md:text-5xl text-[#141210] mt-4">
            Four simple, honest steps
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-7">
          {creditChallenges.steps.map((s) => (
            <div key={s.n} className="relative">
              <div className="font-display text-6xl text-[#e6d6b8] mb-3">{s.n}</div>
              <h3 className="font-display text-2xl text-[#141210] mb-2">{s.title}</h3>
              <p className="text-sm text-[#5c5348] leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Preferred Lender */}
      <section className="bg-[#f4f1ea]">
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-24">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="text-xs tracking-luxury uppercase text-gold">The "Yes" Maker</span>
            <h2 className="font-display text-4xl md:text-5xl text-[#141210] mt-4">
              Meet my flexible lender
            </h2>
          </div>
          <LenderCard />
        </div>
      </section>

      {/* CTA + form */}
      <section className="bg-[#141210]">
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-24 grid grid-cols-1 lg:grid-cols-5 gap-14">
          <div className="lg:col-span-2">
            <h2 className="font-display text-4xl md:text-5xl text-[#f4ebd9] mb-6 leading-tight">
              Start with a no-pressure conversation.
            </h2>
            <p className="text-[#c4b79f] leading-relaxed mb-8">
              Tell me where you are today. There's no judgment here — only a plan to get you the keys.
            </p>
            <Link to="/apply-to-buy">
              <Button className="bg-[#d4af37] hover:bg-[#c19a2e] text-white rounded-full px-8 h-12 tracking-luxury uppercase text-xs">
                Full Application <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
          <div className="lg:col-span-3 bg-[#faf7f2] rounded-3xl p-8 md:p-10">
            <ContactForm />
          </div>
        </div>
      </section>
    </div>
  );
};

export default CreditChallenges;
